/**
 * The inspection register and the conduct screen: the checklist as the report card.
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * WORK_ORDER_M3_CONFIGURATION_AND_SURFACE.md section 4.2 screen 4. The lines are the
 * report card: one outcome each out of the five, comments where they carry one, an
 * attachment per line per ADR-023 through the gated API, and the submit that makes the
 * server judge the results exactly once. The banner that follows names the decision
 * rule and version the server evaluated under, or shows nothing if the server has not
 * said; it never invents a rule.
 *
 * A line the server has locked renders locked, and an attempt to change it is answered
 * by the server's refusal text, not by a client-side gate.
 */

import { useMemo, useState } from "react";
import { Link, useOutletContext, useParams, useSearchParams } from "react-router";

import { ApiError } from "../api/client";
import { downloadUrl, listTargetAttachments, type TargetAttachment } from "../api/attachments";
import { returnResults } from "../api/conduct";
import {
  getVerification,
  listVerifications,
  transitionVerification,
  type Line,
  type VerificationDetail,
  type VerificationSummary,
} from "../api/verifications";
import { listFindings, type FindingSummary } from "../api/findings";
import type { Me } from "../api/me";
import { useToken } from "../auth/sessionContext";
import { RecordCrumb } from "../nav/crumbs";
import { ActionDialog, ToastBar } from "../nav/ActionDialog";
import { ConductDialog, approvalRouteSentences } from "./Conduct";
import { RegisterView } from "../components/RegisterView";
import { strings, term } from "../brand/strings";
import {
  AbsencePanel,
  EmptyState,
  Facts,
  LoadingLine,
  Pagination,
  PageSearch,
  RefusalBox,
  RegisterTh,
  Section,
  StateChip,
  day,
  filterParam,
  filterSet,
  nextSort,
  sortRows,
  stamp,
  type HeaderSort,
} from "./parts";
import { useQuery } from "./useQuery";
import { ServerErrorScope, ServerField } from "../lib/serverErrors";
import { HistoryButton } from "../components/HistoryDrawer";
import { QueueBanner } from "../components/QueueBanner";
import {
  INSPECTION_DONE,
  ProjectFilterBanner,
  useProjectContracts,
  useProjectFilter,
} from "./projectFilter";
import {
  QUEUE_PARAM,
  readAssignedInspections,
  readMyInspectionsQueue,
  readResultApprovalQueue,
} from "../nav/queues";

/**
 * The inspection register's own lifecycle (the vocabulary's
 * "verification_workflow_state" tokens). The State dropdown lists THESE and not the
 * global workflow vocabulary, most of whose states no inspection can ever carry.
 */
const VERIFICATION_REGISTER_STATES = [
  "DRAFT",
  "SUBMITTED",
  "APPROVED",
  "DISTRIBUTED",
  "IN_ACTION",
  "FINAL_APPROVAL",
  "CLOSED",
  "REJECTED",
  "CANCELLED",
] as const;

export function inspectionRegisterStateLabel(record: {
  workflowState: string;
  resultsApprovedAt: string | null;
}): string {
  if (record.workflowState === "FINAL_APPROVAL") {
    if (record.resultsApprovedAt !== null) {
      return strings.inspections.statusResultsApproved;
    }
    return strings.inspections.statusWaitingApproval;
  }
  return term(record.workflowState);
}

export function InspectionsPage(): React.JSX.Element {
  const token = useToken();
  // The stage/discipline matrix filters are gone (M5 P5c #6): the demo seed's
  // verification types carry no stage and no discipline on the rows, so a filter
  // whose rows carry no axis does not render. The state filter lives in the State
  // column header now (M5 P5c #7).
  const [parameters, setParameters] = useSearchParams();
  const stateFilter = parameters.get("state");
  // Multi-select column filter, comma-joined in the URL (M5 P6 seat V #6/#8). THE
  // SEAM: the backend list filter takes a single value only, so one token goes to
  // the server and a multi-select narrows the fetched page client-side.
  const searchText = parameters.get("q") ?? "";
  // Wave b12 frontend item 12: a Home card's "Inspections in progress" opens the register
  // narrowed to the project's contracts and to the records not yet over.
  const project = useProjectFilter();
  const projectContracts = useProjectContracts(project?.id ?? null);
  const inProgressOnly = parameters.get("progress") === "open";

  const stateSet = filterSet(stateFilter);
  // Wave W1 (R13): `?queue=approve` and `?queue=mine` open the register as a queue,
  // through the SAME read Home counts. A state filter then narrows the queue on screen.
  const queueParam = parameters.get("queue");
  const queue =
    queueParam === QUEUE_PARAM.resultApproval
      ? { read: readResultApprovalQueue, label: strings.queues.resultApproval.label }
      : queueParam === QUEUE_PARAM.myInspections
        ? { read: readMyInspectionsQueue, label: strings.queues.myInspections.label }
        : queueParam === QUEUE_PARAM.assignedInspections
          ? { read: readAssignedInspections, label: strings.queues.assignedInspections }
          : null;
  const register = useQuery(
    async (signal) =>
      queue !== null
        ? { items: await queue.read(token, signal) }
        : listVerifications(token, {
            workflowState: stateSet.length === 1 ? (stateSet[0] ?? null) : null,
            signal,
          }),
    [token, stateFilter, queueParam],
  );

  const setParameter = (name: string, value: string): void => {
    setParameters((current) => {
      const next = new URLSearchParams(current);
      next.delete("offset");
      if (value.length === 0) {
        next.delete(name);
      } else {
        next.set(name, value);
      }
      return next;
    });
  };

  // The chart contract's click-through lands here as ?state=: the register honours a
  // workflow-state filter carried in the URL, so KPI tiles and charts can deep-link it.
  // The state filter rides the server call now; the text search narrows the loaded
  // page (the server's list endpoint carries no text parameter).
  const search = searchText.trim().toLowerCase();
  const sort: HeaderSort = {
    key: parameters.get("sort"),
    dir: parameters.get("dir") === "desc" ? "desc" : "asc",
  };
  const sortOn = (key: string): void => {
    const next = nextSort(sort, key);
    setParameters((current) => {
      const merged = new URLSearchParams(current);
      merged.delete("offset");
      if (next.key === null) {
        merged.delete("sort");
      } else {
        merged.set("sort", next.key);
      }
      if (next.dir === "desc") {
        merged.set("dir", "desc");
      } else {
        merged.delete("dir");
      }
      return merged;
    });
  };
  const items = sortRows(
    register.kind === "ready"
      ? register.data.items.filter(
          (record) =>
            (search.length === 0 ||
              record.humanId.toLowerCase().includes(search) ||
              record.title.toLowerCase().includes(search) ||
              record.contractorPartyName.toLowerCase().includes(search)) &&
            (stateSet.length === 0 || stateSet.includes(record.workflowState)) &&
            (projectContracts === null ||
              (record.contractId !== null && projectContracts.has(record.contractId))) &&
            (!inProgressOnly || !INSPECTION_DONE.includes(record.workflowState)),
        )
      : [],
    sort,
    {
      record: (row) => row.humanId,
      date: (row) => row.plannedDate,
      contractor: (row) => row.contractorPartyName,
      state: (row) => inspectionRegisterStateLabel(row),
      outcome: (row) => (row.outcome === null ? "" : term(row.outcome)),
    },
  );

  const PAGE_SIZE = 25;
  const offset = Number(parameters.get("offset")) || 0;
  const pagedItems = items.slice(offset, offset + PAGE_SIZE);

  return (
    <>
      <h2 className="q-shell__heading">{strings.inspections.heading}</h2>
      <p className="q-shell__lede">{strings.inspections.lede}</p>

      {queue !== null && (
        <QueueBanner
          label={queue.label}
          count={register.kind === "ready" ? register.data.items.length : null}
          wholeRegister="/inspections"
        />
      )}
      {project !== null && (
        <ProjectFilterBanner
          label={project.label}
          view={inProgressOnly ? strings.home.cardInspectionsOpen : strings.inspections.heading}
          register="/inspections"
        />
      )}

      <RegisterView
        toolbar={
          <div className="q-row">
            <PageSearch
              id="inspection-search"
              value={searchText}
              onChange={(next) => {
                setParameter("q", next);
              }}
            />
          </div>
        }
        loading={register.kind === "loading"}
        absence={register.kind === "absence"}
        failed={register.kind === "failed"}
        empty={register.kind === "ready" && items.length === 0}
        emptyTitle={strings.inspections.emptyTitle}
        emptyBody={strings.inspections.emptyBody}
        testId="inspections-register"
        caption={strings.inspections.heading}
        columns={
          <>
            <RegisterTh
              label={strings.inspections.columnRecord}
              sortKey="record"
              sort={sort}
              onSort={sortOn}
            />
            <RegisterTh
              label={strings.inspections.columnDate}
              sortKey="date"
              sort={sort}
              onSort={sortOn}
            />
            <RegisterTh
              label={strings.inspections.columnContractor}
              sortKey="contractor"
              sort={sort}
              onSort={sortOn}
            />
            <RegisterTh
              label={strings.inspections.columnState}
              sortKey="state"
              sort={sort}
              onSort={sortOn}
              filter={{
                values: stateSet,
                options: VERIFICATION_REGISTER_STATES.map((value) => ({
                  value,
                  label: term(value),
                })),
                onChange: (next) => {
                  setParameter("state", filterParam(next));
                },
              }}
            />
            <RegisterTh
              label={strings.inspections.columnOutcome}
              sortKey="outcome"
              sort={sort}
              onSort={sortOn}
            />
          </>
        }
        pagination={
          register.kind === "ready" && items.length > 0 ? (
            <Pagination
              total={items.length}
              limit={PAGE_SIZE}
              offset={offset}
              onMove={(nextOffset) => {
                const merged = new URLSearchParams(parameters);
                if (nextOffset === 0) {
                  merged.delete("offset");
                } else {
                  merged.set("offset", String(nextOffset));
                }
                setParameters(merged);
              }}
            />
          ) : null
        }
      >
        {pagedItems.map((record: VerificationSummary) => (
          <tr key={record.id}>
            <td>
              <Link to={`/inspections/${record.id}`} className="q-register__mono">
                {record.humanId}
              </Link>
              <div className="q-register__wrap">{record.title}</div>
            </td>
            <td>{day(record.plannedDate)}</td>
            <td>{record.contractorPartyName}</td>
            <td>
              <StateChip
                state={record.workflowState}
                label={inspectionRegisterStateLabel(record)}
              />
            </td>
            <td>{record.outcome !== null ? term(record.outcome) : ""}</td>
          </tr>
        ))}
      </RegisterView>
    </>
  );
}

/**
 * The numbered section a bank line sits under, read from the leading numbering its
 * description carries ("1.1.3 The welding procedure..." sits under section 1.1). Lines
 * loaded by the inspection day carry their section on the line itself; this reading
 * stays for the older records whose descriptions carry the numbering.
 */
export function sectionOf(description: string): string | null {
  const match = /^(\d+(?:\.\d+)+)\s/.exec(description);
  if (match?.[1] === undefined) {
    return null;
  }
  const parts = match[1].split(".");
  const section = parts.slice(0, -1).join(".");
  return section.length > 0 ? section : null;
}

/** The section heading a line renders under: its own, else the numbering it carries. */
function sectionHeadingOf(line: Line): string | null {
  if (line.sectionNumber !== null || line.sectionTitle !== null) {
    return strings.conduct.sectionHeading(line.sectionNumber, line.sectionTitle);
  }
  return sectionOf(line.itemDescription);
}

/** Where the inspection came from, in plain words (the downstream trace, both ways). */
function originLine(detail: VerificationDetail): React.ReactNode {
  if (detail.origin === "PLANNED_ACTIVITY" && detail.scheduledActivityId !== null) {
    return (
      <Link className="q-link" to={`/activities/${detail.scheduledActivityId}`}>
        {strings.inspections.originActivity}
      </Link>
    );
  }
  if (detail.origin === "OPERATOR_REQUEST") {
    return (
      <Link className="q-link" to={`/rfis/${detail.id}`}>
        {strings.inspections.originOperatorRequest}
      </Link>
    );
  }
  return (
    <Link className="q-link" to={`/rfis/${detail.id}`}>
      {strings.inspections.originRfi}
    </Link>
  );
}

export function InspectionDetailPage(): React.JSX.Element {
  const token = useToken();
  const { me } = useOutletContext<{ me: Me }>();
  const { verificationId = "" } = useParams();
  const [reload, setReload] = useState(0);
  const [actionError, setActionError] = useState<ApiError | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [grouped, setGrouped] = useState(false);
  const [conducting, setConducting] = useState(false);
  const [deciding, setDeciding] = useState<"APPROVE" | "RETURN" | null>(null);

  const record = useQuery(
    () => getVerification(token, verificationId),
    [token, verificationId, reload],
  );

  const reread = (): void => {
    setReload((n) => n + 1);
  };

  // The findings born on this record's lines, read once through the register's own
  // verification filter; the map keys a line to the finding it bore. When the finding
  // was promoted, the action request rides on the same row.
  const lineFindings = useQuery(
    (signal) => listFindings(token, { verificationId: verificationId, signal }),
    [token, verificationId, reload],
  );
  const findingsByLine = useMemo(() => {
    const map = new Map<string, FindingSummary>();
    if (lineFindings.kind === "ready") {
      for (const finding of lineFindings.data.items) {
        map.set(finding.verificationLineId, finding);
      }
    }
    return map;
  }, [lineFindings]);

  async function acknowledge(): Promise<void> {
    setActionError(null);
    try {
      await transitionVerification(token, verificationId, "acknowledge");
      reread();
    } catch (error) {
      if (error instanceof ApiError) {
        setActionError(error);
      }
    }
  }

  async function handleClose(): Promise<void> {
    setActionError(null);
    try {
      await transitionVerification(token, verificationId, "close");
      setToast(strings.conduct.closedSuccess);
      reread();
    } catch (error) {
      if (error instanceof ApiError) {
        setActionError(error);
      }
    }
  }

  if (record.kind === "absence") {
    return (
      <>
        <h2 className="q-shell__heading">{strings.inspections.detailHeading}</h2>
        <AbsencePanel />
      </>
    );
  }
  if (record.kind !== "ready") {
    return (
      <>
        <h2 className="q-shell__heading">{strings.inspections.detailHeading}</h2>
        {record.kind === "loading" && <LoadingLine label={strings.common.loading} />}
        {record.kind === "failed" && (
          <div className="q-refusal" role="alert">
            <p className="q-refusal__heading">{strings.common.failed}</p>
          </div>
        )}
      </>
    );
  }

  const detail: VerificationDetail = record.data;
  const judged = detail.outcome !== null;
  const evaluated = detail.resultBanner?.evaluatedUnder ?? null;
  const acts = detail.conductActs;
  const mayConduct = acts.includes("START") || acts.includes("SAVE");
  const contractor = me.workflowClass === "contractor";
  const contractorAcknowledged = detail.transitions.some((t) => t.event === "ACKNOWLEDGE");
  const isApproved = detail.resultsApprovedAt !== null;
  const awaitingClose =
    detail.workflowState === "FINAL_APPROVAL" &&
    isApproved &&
    contractorAcknowledged &&
    detail.closedAt === null;

  const approveTransition = detail.transitions.find((t) => t.event === "APPROVE_RESULTS");
  const approverAssessor = detail.assessors.find(
    (a) => a.personDisplayName === approveTransition?.actorDisplayName,
  );
  const approverRole =
    approverAssessor?.roleLabel ?? approveTransition?.actorDisplayName ?? "QC lead";
  const approvedDate = day(detail.resultsApprovedAt ?? approveTransition?.at ?? null);

  const openFindings = (lineFindings.kind === "ready" ? lineFindings.data.items : []).filter(
    (f) => f.findingStatus !== "CLOSED" && f.findingStatus !== "RECORDED",
  );
  const openPunchItems = openFindings.filter((f) => f.punchListId !== null);
  const openOtherFindings = openFindings.filter((f) => f.punchListId === null);
  const blockingItems: string[] = [];
  if (openPunchItems.length > 0) {
    blockingItems.push(
      `${String(openPunchItems.length)} open punch item${openPunchItems.length === 1 ? "" : "s"} (${openPunchItems.map((p) => p.humanId).join(", ")})`,
    );
  }
  if (openOtherFindings.length > 0) {
    blockingItems.push(
      `${String(openOtherFindings.length)} open finding${openOtherFindings.length === 1 ? "" : "s"} (${openOtherFindings.map((f) => f.humanId).join(", ")})`,
    );
  }

  const cannotApprove = detail.workflowState === "FINAL_APPROVAL" && !acts.includes("APPROVE");

  return (
    <ServerErrorScope error={actionError}>
      {/* Wave W2: the inspection is named on the shell's one breadcrumb trail. */}
      <RecordCrumb label={detail.humanId} />
      <h2 className="q-shell__heading" data-testid="verification-title">
        {detail.humanId} {detail.title}
      </h2>
      <p className="q-shell__lede">
        <StateChip
          state={detail.workflowState}
          {...(detail.workflowState === "FINAL_APPROVAL"
            ? {
                label: awaitingClose
                  ? strings.inspections.statusAwaitingClose
                  : isApproved
                    ? strings.inspections.statusResultsApproved
                    : strings.inspections.statusWaitingApproval,
              }
            : {})}
        />
        {detail.outcome !== null && (
          <span>
            {" "}
            · {strings.inspections.outcomeHeader}: <StateChip state={detail.outcome} />
          </span>
        )}
        {detail.closedWithRejections && (
          <span>
            {" "}
            ·{" "}
            <span
              className="q-chip q-chip--attention"
              data-testid="closed-with-rejections"
              title={strings.inspections.closedWithRejectionsHint}
            >
              {strings.inspections.closedWithRejections}
            </span>
          </span>
        )}
      </p>
      {detail.closedWithRejections && (
        <p className="q-note" data-testid="closed-with-rejections-note">
          {strings.inspections.closedWithRejectionsHint}
        </p>
      )}
      {toast !== null && (
        <ToastBar
          message={toast}
          onDone={() => {
            setToast(null);
          }}
        />
      )}

      {judged && (
        <div
          className={`q-banner${detail.outcome === "REJECTED" ? "" : " q-banner--success"}`}
          role="status"
          data-testid="results-banner"
        >
          <p className="q-banner__heading">{strings.inspections.resultsBannerHeading}</p>
          <p className="q-thread__body">
            {strings.inspections.outcomeHeader}: {term(detail.outcome)}
          </p>
          {evaluated !== null && (
            <span className="q-banner__rule">
              {strings.inspections.evaluatedUnder(evaluated.rule)}
            </span>
          )}
        </div>
      )}
      {actionError !== null && <RefusalBox error={actionError} />}

      <div className="q-section-bands" data-testid="inspection-detail-sections">
        <Section heading={strings.inspections.detailHeading}>
          <Facts
            entries={[
              [strings.common.humanId, <span key="id">{detail.humanId}</span>],
              [strings.inspections.originLabel, <span key="origin">{originLine(detail)}</span>],
              [strings.inspections.columnDate, <span key="date">{day(detail.plannedDate)}</span>],
              [strings.inspections.locationLabel, <span key="loc">{detail.location ?? ""}</span>],
              [
                strings.inspections.componentLabel,
                <span key="component">{detail.inspectedComponent ?? ""}</span>,
              ],
              [
                strings.inspections.procedureLabel,
                <span key="procedure">{detail.procedureDrawing ?? ""}</span>,
              ],
            ]}
          />
          <div className="q-actions-row q-gap-top" data-testid="conduct-actions">
            <HistoryButton
              objectType="VERIFICATION"
              objectId={detail.id}
              recordLabel={detail.humanId}
            />
            {mayConduct && (
              <button
                type="button"
                className="q-button q-mini--primary"
                data-testid="conduct-open"
                onClick={() => {
                  setConducting(true);
                }}
              >
                {acts.includes("SAVE") ? strings.conduct.continueDraft : strings.conduct.begin}
              </button>
            )}
            {acts.includes("APPROVE") && (
              <button
                type="button"
                className="q-button q-mini--primary"
                data-testid="results-approve"
                onClick={() => {
                  setDeciding("APPROVE");
                }}
              >
                {strings.conduct.approve}
              </button>
            )}
            {acts.includes("RETURN") && (
              <button
                type="button"
                className="q-button q-button--quiet"
                data-testid="results-return"
                onClick={() => {
                  setDeciding("RETURN");
                }}
              >
                {strings.conduct.returnResults}
              </button>
            )}
            {isApproved && (
              <p className="q-note" data-testid="results-approved-notice">
                {strings.conduct.resultsApprovedBy(approverRole, approvedDate)}
              </p>
            )}
            {cannotApprove && !contractor && (
              <p className="q-note" data-testid="results-waiting">
                {isApproved
                  ? strings.conduct.cannotApproveAlreadyApproved(approverRole, approvedDate)
                  : strings.conduct.cannotApproveOutranked(
                      detail.approvalRoute === null
                        ? strings.conduct.approvalWaits
                        : approvalRouteSentences(detail.approvalRoute).join(" "),
                    )}
              </p>
            )}
            {contractor &&
              detail.workflowState === "FINAL_APPROVAL" &&
              isApproved &&
              !contractorAcknowledged && (
                <button
                  type="button"
                  className="q-button q-mini--primary"
                  data-testid="results-acknowledge"
                  onClick={() => {
                    void acknowledge();
                  }}
                >
                  {strings.inspections.acknowledge}
                </button>
              )}
            {!contractor && isApproved && !contractorAcknowledged && (
              <p className="q-note" data-testid="awaiting-ack-notice">
                {strings.conduct.awaitingAcknowledgement}
              </p>
            )}
            {awaitingClose && (
              <div data-testid="awaiting-close-container">
                {blockingItems.length > 0 ? (
                  <p className="q-note" data-testid="awaiting-close-notice">
                    {strings.conduct.awaitingCloseBlocked(blockingItems.join(", "))}
                  </p>
                ) : (
                  <p className="q-note q-note--success" data-testid="awaiting-close-notice">
                    {strings.conduct.awaitingCloseReady}
                  </p>
                )}
                {!contractor && (
                  <button
                    type="button"
                    className="q-button q-mini--primary"
                    data-testid="verification-close"
                    disabled={blockingItems.length > 0}
                    onClick={() => {
                      void handleClose();
                    }}
                  >
                    {strings.conduct.closeVerification}
                  </button>
                )}
              </div>
            )}
          </div>
        </Section>

        <Section heading={strings.inspections.linesHeading}>
          <OutcomeStrip lines={detail.lines} />
          <GroupToggle
            grouped={grouped}
            onToggle={() => {
              setGrouped((value) => !value);
            }}
          />
          {detail.lines.length === 0 && (
            <EmptyState title={strings.common.empty} body={strings.inspections.emptyBody} />
          )}
          <div className="q-thread">
            {(grouped ? groupByOutcome(detail.lines) : [null]).map((group, index) => (
              <div key={group ?? `flat-${String(index)}`}>
                {group !== null && (
                  <h4 className="q-section__heading" data-testid="outcome-group">
                    {group === "" ? strings.inspections.unrecordedGroup : term(group)}
                  </h4>
                )}
                {(group === null
                  ? detail.lines
                  : detail.lines.filter((line: Line) => (line.outcomeValue ?? "") === group)
                ).map((line: Line, index: number, siblings: Line[]) => {
                  // The checklist's numbered sections render as headings between the lines.
                  const section = sectionHeadingOf(line);
                  const before = siblings[index - 1];
                  const previous =
                    index > 0 && before !== undefined ? sectionHeadingOf(before) : null;
                  const heading = section !== null && section !== previous ? section : null;
                  return (
                    <div key={line.id}>
                      {heading !== null && (
                        <h4 className="q-section__heading" data-testid="bank-section">
                          {heading}
                        </h4>
                      )}
                      <LineCard line={line} finding={findingsByLine.get(line.id)} />
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </Section>

        <Section heading={strings.itps.historyHeading}>
          <div className="q-thread">
            {detail.transitions.map((entry) => (
              <article className="q-thread__entry" key={entry.id}>
                <p className="q-thread__meta">
                  <strong>{term(entry.event)}</strong> · {term(entry.fromState)} →{" "}
                  {term(entry.toState)} · {stamp(entry.at)}
                  {entry.actorDisplayName !== null && ` · ${entry.actorDisplayName}`}
                </p>
                {entry.comment !== null && <p className="q-thread__body">{entry.comment}</p>}
              </article>
            ))}
          </div>
        </Section>
      </div>

      {conducting && (
        <ConductDialog
          verificationId={detail.id}
          onClose={() => {
            setConducting(false);
            reread();
          }}
          onChanged={(message) => {
            if (message !== "") {
              setConducting(false);
              setToast(message);
            }
            reread();
          }}
        />
      )}
      {deciding !== null && (
        <ResultsDecisionDialog
          verificationId={detail.id}
          humanId={detail.humanId}
          decision={deciding}
          onClose={() => {
            setDeciding(null);
          }}
          onDone={(message) => {
            setDeciding(null);
            setToast(message);
            reread();
          }}
        />
      )}
    </ServerErrorScope>
  );
}

/** Approve the submitted results, or return them with the reason (the approval cycle). */
export function ResultsDecisionDialog({
  verificationId,
  humanId,
  decision,
  onClose,
  onDone,
}: {
  readonly verificationId: string;
  readonly humanId: string;
  readonly decision: "APPROVE" | "RETURN";
  readonly onClose: () => void;
  readonly onDone: (message: string) => void;
}): React.JSX.Element {
  const token = useToken();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const approving = decision === "APPROVE";

  async function send(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      if (approving) {
        await transitionVerification(token, verificationId, "approve_results");
        onDone(strings.conduct.approved);
      } else {
        await returnResults(token, verificationId, reason.trim());
        onDone(strings.conduct.returned);
      }
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <ActionDialog
      open
      title={`${humanId}: ${approving ? strings.conduct.approveHeading : strings.conduct.returnHeading}`}
      onClose={onClose}
      error={error ?? undefined}
    >
      {approving ? (
        <p className="q-shell__lede">{strings.conduct.approveLede}</p>
      ) : (
        <div className="q-field">
          <label className="q-field__label" htmlFor="results-return-reason">
            {strings.conduct.returnReasonLabel}
          </label>
          <ServerField field="reason">
            <textarea
              id="results-return-reason"
              className="q-textarea"
              data-testid="results-return-reason"
              value={reason}
              onChange={(event) => {
                setReason(event.target.value);
              }}
            />
          </ServerField>
        </div>
      )}
      <div className="q-actions-row">
        <button
          type="button"
          className="q-button q-mini--primary"
          data-testid="results-decision-send"
          disabled={busy || (!approving && reason.trim() === "")}
          onClick={() => {
            void send();
          }}
        >
          {approving ? strings.conduct.approve : strings.conduct.returnResults}
        </button>
        <button type="button" className="q-button q-button--quiet" onClick={onClose}>
          {strings.common.cancel}
        </button>
      </div>
    </ActionDialog>
  );
}

function LineCard({
  line,
  finding,
}: {
  line: Line;
  /** The finding born on this line, when the register answers with one. */
  finding: FindingSummary | undefined;
}): React.JSX.Element {
  const token = useToken();
  const attachments = useQuery(
    () => listTargetAttachments(token, { entityType: "VERIFICATION_LINE", entityId: line.id }),
    [token, line.id],
  );
  const locked = line.lockedAt !== null;

  return (
    <article className="q-line" data-testid="line-row">
      <div className="q-line__head">
        <span className="q-line__no">
          {line.lineNumber ?? `${strings.inspections.lineNo} ${String(line.lineNo)}`}
        </span>
        {locked && (
          <span className="q-chip" data-testid="line-locked">
            {strings.inspections.lockedBadge}
          </span>
        )}
        {line.isPositiveObservation && (
          <span className="q-chip q-chip--success">{strings.inspections.positiveBadge}</span>
        )}
        {line.outcomeValue !== null && <StateChip state={line.outcomeValue} />}
      </div>

      <p className="q-line__text">{line.itemDescription}</p>
      <p className="q-line__criteria">
        {strings.inspections.acceptance}: {line.acceptanceCriteria}
      </p>
      {line.comments !== null && <p className="q-thread__body">{line.comments}</p>}

      {finding !== undefined && (
        <p className="q-row" data-testid="line-finding">
          <Link className="q-link" to={`/findings/${finding.id}`}>
            {strings.inspections.bornFinding}: {finding.humanId}
          </Link>
          {finding.actionRequestId !== null && (
            <Link
              className="q-link"
              to={`/actions/${finding.actionRequestId}`}
              data-testid="line-action"
            >
              {strings.inspections.actionFromFinding}
            </Link>
          )}
        </p>
      )}

      <div className="q-attachments" data-testid="line-attachments">
        {attachments.kind === "ready" &&
          (attachments.data.length === 0 ? (
            <p className="q-attachment">{strings.inspections.attachEmpty}</p>
          ) : (
            attachments.data.map((entry: TargetAttachment) => (
              <p className="q-attachment" key={entry.linkId} data-testid="line-attachment">
                <DownloadLink attachmentId={entry.attachmentId} filename={entry.filename} /> (
                {String(Math.max(1, Math.round(entry.fileSizeBytes / 1024)))} kB)
              </p>
            ))
          ))}
      </div>
    </article>
  );
}

/**
 * A download that stays inside the gated API. The endpoint needs the bearer credential,
 * so the file is fetched with the session's token and handed to the browser as a blob;
 * A download that is a real link and stays inside the gated API. The anchor's href
 * names the attachments API's download path; the endpoint needs the bearer credential,
 * so the click is answered by a fetch with the session's token handed to the browser
 * as a blob — there is no unauthenticated URL to hand out and none is invented.
 */
export function DownloadLink({
  attachmentId,
  filename,
}: {
  attachmentId: string;
  filename: string;
}): React.JSX.Element {
  const token = useToken();
  const [busy, setBusy] = useState(false);
  return (
    <a
      className="q-link"
      href={downloadUrl(attachmentId)}
      download={filename}
      aria-disabled={busy}
      data-testid="attachment-download-link"
      onClick={(event) => {
        event.preventDefault();
        if (busy) {
          return;
        }
        setBusy(true);
        fetch(`/api/attachments/${attachmentId}/download`, {
          headers: { Authorization: `Bearer ${token}` },
        })
          .then(async (response) => {
            if (!response.ok) {
              throw new Error("The download was refused");
            }
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const anchor = document.createElement("a");
            anchor.href = url;
            anchor.download = filename;
            anchor.click();
            URL.revokeObjectURL(url);
          })
          .catch(() => undefined)
          .finally(() => {
            setBusy(false);
          });
      }}
    >
      {filename}
    </a>
  );
}

/* ---------------------------------------------------------------------- */
/* The outcome distribution strip and the group-by-outcome view (the UI    */
/* direction document section 4). Both compute from the lines already      */
/* fetched; there is no extra read and no backend work.                    */
/* ---------------------------------------------------------------------- */

/** The counts per recorded outcome over the record's own lines. */
function outcomeCounts(lines: readonly Line[]): { value: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const line of lines) {
    const value = line.outcomeValue ?? "";
    counts.set(value, (counts.get(value) ?? 0) + 1);
  }
  return [...counts.entries()].map(([value, count]) => ({ value, count }));
}

function OutcomeStrip({ lines }: { lines: readonly Line[] }): React.JSX.Element {
  const counts = outcomeCounts(lines);
  if (counts.length === 0) {
    return <p className="q-note">{strings.inspections.distributionEmpty}</p>;
  }
  return (
    <div
      className="q-row"
      data-testid="outcome-strip"
      aria-label={strings.inspections.distributionHeading}
    >
      {counts.map(({ value, count }) => (
        <span key={value} className="q-chip" data-testid="outcome-count">
          {value === "" ? strings.inspections.unrecordedGroup : term(value)}: {String(count)}
        </span>
      ))}
    </div>
  );
}

function GroupToggle({
  grouped,
  onToggle,
}: {
  grouped: boolean;
  onToggle: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      className="q-button q-button--quiet"
      data-testid="group-toggle"
      onClick={onToggle}
    >
      {grouped ? strings.inspections.flatToggle : strings.inspections.groupToggle}
    </button>
  );
}

/** The line outcomes in their canonical order (the engine's five). */
const OUTCOME_VALUES = [
  "ACCEPTED",
  "ACCEPTED_WITH_COMMENTS",
  "REJECTED",
  "CANCELLED",
  "NOT_ATTENDED",
] as const;

/** The groups in the canonical outcome order, the unrecorded lines last. */
function groupByOutcome(lines: readonly Line[]): (string | null)[] {
  const present = new Set(lines.map((line) => line.outcomeValue ?? ""));
  const groups: (string | null)[] = OUTCOME_VALUES.filter((value) => present.has(value));
  if (present.has("")) {
    groups.push("");
  }
  return groups;
}
